import type { CreateVesselDto, PS07Response, UpdateVesselDto, VD02Response, VesselDto, VesselListDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';

@Injectable({
  providedIn: 'root',
})
export class VesselService {
  private restService = inject(RestService);
  apiName = 'Default';
  

  create = (input: CreateVesselDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselDto>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/vessel/${id}`,
    },
    { apiName: this.apiName,...config });
  

  fetchVesselParticularsById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VD02Response>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}/fetch-vessel-particulars`,
    },
    { apiName: this.apiName,...config });
  

  fetchVesselParticularsByImoNumberAndMmsi = (imoNumber: string, mmsi: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VD02Response>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/fetch-vessel-particulars',
      params: { imoNumber, mmsi },
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselListDto[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<VesselDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<VesselListDto>>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/vessel/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/schema',
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselDto>({
      method: 'GET',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/vessel/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  singleVesselParticularsByImoNumberAndMmsi = (imoNumber: string, mmsi: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PS07Response>({
      method: 'POST',
      headers: { Accept: 'application/json' },
      url: '/api/app/vessel/single-vessel-particulars',
      params: { imoNumber, mmsi },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateVesselDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, VesselDto>({
      method: 'PUT',
      headers: { Accept: 'application/json' },
      url: `/api/app/vessel/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
}